"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  index: "P.01",
  title: "Trasparenza sui Dati",
  description: "La nostra guida utilizza cookie tecnici essenziali per garantire la navigazione sicura e il corretto funzionamento del sito. Non raccogliamo dati personali sensibili senza il tuo esplicito consenso, mantenendo la massima riservatezza per i pazienti.",
  linkLabel: "Leggi la Privacy Policy",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/dati_paziente_sicurezza.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/dati_paziente_sicurezza.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/dati_paziente_sicurezza.png-wvc-sizes",
    alt: "Interfaccia di sicurezza dati medici con crittografia",
    label: "PROTEZIONE DATI"
  },
  imageRight: true
}, {
  index: "P.02",
  title: "Cookie di Analisi",
  description: "Utilizziamo strumenti di analisi anonimizzati per comprendere come i pazienti interagiscono con la nostra guida. Questo ci permette di migliorare costantemente le informazioni sugli impianti dentali in Albania, rendendole più chiare e accessibili.",
  linkLabel: "Gestisci preferenze",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/analisi_statistiche_mediche.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/analisi_statistiche_mediche.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/analisi_statistiche_mediche.png-wvc-sizes",
    alt: "Grafico di analisi dati con gradiente blu medico",
    label: "STATISTICHE / 2026"
  },
  imageRight: false
}, {
  index: "P.03",
  title: "Cookie di Terze Parti",
  description: "Alcune funzionalità, come le mappe per raggiungere le cliniche o i widget di contatto WhatsApp, potrebbero utilizzare cookie di terze parti. Questi sono gestiti direttamente dai fornitori esterni nel rispetto delle normative vigenti.",
  linkLabel: "Vedi fornitori esterni",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/mappa_cliniche_albania.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/mappa_cliniche_albania.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/mappa_cliniche_albania.png-wvc-sizes",
    alt: "Mappa stilizzata delle cliniche dentali in Albania",
    label: "MAPPA / TIR-01"
  },
  imageRight: true
}];
function CornerTicks() {
  return <>
      <span className="pointer-events-none absolute -top-px -left-px h-2.5 w-2.5 border-t border-l border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -top-px -right-px h-2.5 w-2.5 border-t border-r border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -bottom-px -left-px h-2.5 w-2.5 border-b border-l border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -bottom-px -right-px h-2.5 w-2.5 border-b border-r border-[var(--primary)]" aria-hidden="true" />
    </>;
}
function VioletBeam({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center gap-2 ${className}`} aria-hidden="true">
      <div className="h-px flex-1 bg-border" />
      <div className="h-1.5 w-1.5 rounded-md bg-[var(--primary)]" />
    </div>;
}
export default function CookiePolicyDetails() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1888" id="cookiepolicydetails" className="relative bg-background text-foreground py-28 md:py-36 overflow-hidden">
      <div ref={header.ref} className={`relative mx-auto max-w-[880px] px-5 md:px-8 text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-[calc(50%+60px)] font-default font-semibold text-[12rem] md:text-[22rem] leading-none tracking-tighter select-none" style={{
        WebkitTextStroke: "1px var(--border)",
        color: "transparent"
      }}>
          02
        </span>

        <div className="relative z-10">
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.12em] text-muted-foreground mb-6">
            Informativa Trasparente
          </p>
          <h2 className="font-default font-semibold text-[clamp(2.25rem,4.4vw,4rem)] leading-[1.02] tracking-tight">
            Gestione dei{" "}
            <span className="italic" style={{
            WebkitTextStroke: "1px var(--primary)",
            color: "transparent"
          }}>
              Cookie
            </span>
            .
          </h2>
          <VioletBeam className="mt-8 mx-auto max-w-xs" />
        </div>
      </div>

      <div className="relative mx-auto max-w-[1320px] px-5 md:px-8 mt-20 md:mt-28">
        {features.map((feature, i) => {
        const row = useRevealOnScroll<HTMLDivElement>({
          threshold: 0.15
        });
        const imageOnRight = feature.imageRight;
        return <div key={i} data-index={i}>
              <div ref={row.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center transition-all duration-700 ease-out ${row.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <div className={`lg:col-span-7 ${imageOnRight ? "lg:order-1" : "lg:order-2"}`}>
                  <p className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--primary)] mb-4">
                    {feature.index}
                  </p>
                  <h2 className="font-default font-medium text-[clamp(1.5rem,2vw,2rem)] leading-[1.15] tracking-tight mb-5">
                    {feature.title}
                  </h2>
                  <p className="font-default text-[clamp(1.0625rem,1.25vw,1.25rem)] leading-[1.6] text-muted-foreground max-w-xl mb-8">
                    {feature.description}
                  </p>
                  <Link to="/PrivacyPolicyPage" className="group inline-flex items-center gap-2 text-foreground hover:text-[var(--primary)] transition-colors duration-200">
                    <span className="font-default font-medium text-sm md:text-base">
                      {feature.linkLabel}
                    </span>
                    <ArrowRight className="size-4 text-[var(--primary)] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>

                <div className={`lg:col-span-5 relative ${imageOnRight ? "lg:order-2" : "lg:order-1"}`}>
                  <div aria-hidden="true" className={`pointer-events-none absolute -z-10 h-[420px] w-[420px] rounded-lg blur-3xl opacity-50 md:opacity-100 ${imageOnRight ? "-bottom-20 -left-20" : "-bottom-20 -right-20"}`} style={{
                background: "radial-gradient(closest-side, hsl(217 91% 60% / 0.35), transparent 70%)"
              }} />

                  <div className="group relative">
                    <div className="relative border border-border bg-card overflow-hidden rounded-md transition-colors duration-300 group-hover:border-[var(--primary)]">
                      <CornerTicks />

                      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-slate-50">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-md bg-slate-200" />
                          <span className="h-2 w-2 rounded-md bg-slate-200" />
                          <span className="h-2 w-2 rounded-md bg-slate-200" />
                        </div>
                        <p className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground uppercase">
                          {feature.image.label}
                        </p>
                        <span className="font-mono text-[10px] tracking-[0.12em] text-[var(--primary)] uppercase">
                          ATTIVO
                        </span>
                      </div>

                      <div className="overflow-hidden">
                        <img src={feature.image.src} data-wvc-srcset={feature.image.srcset} data-wvc-sizes={feature.image.sizes} alt={feature.image.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {i < features.length - 1 && <VioletBeam className="my-16 md:my-24" />}
            </div>;
      })}
      </div>
    </section>;
}